import React from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  Chip,
} from '@material-ui/core';
import { EventInfo } from '../types';

interface KubernetesEventsTimelineProps {
  events: EventInfo[];
  loading?: boolean;
}

function formatTime(timestamp: string): string {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  return date.toLocaleString('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export const KubernetesEventsTimeline: React.FC<KubernetesEventsTimelineProps> = ({
  events,
  loading,
}) => {
  const warnings = events.filter(e => e.type === 'Warning').length;

  return (
    <Card>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <span>Events ({events.length})</span>
            {warnings > 0 && (
              <Chip
                label={`${warnings} Warning`}
                size="small"
                style={{
                  backgroundColor: '#fff3e0',
                  color: '#e65100',
                  border: '1px solid #ffcc80',
                  fontSize: '0.7rem',
                  height: 20,
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        }
        titleTypographyProps={{ variant: 'subtitle1', style: { fontWeight: 600 } }}
        style={{ paddingBottom: 0 }}
      />
      <CardContent style={{ padding: 0 }}>
        {loading ? (
          <Box p={2}>
            <Typography variant="body2" color="textSecondary">加载中...</Typography>
          </Box>
        ) : events.length === 0 ? (
          <Box p={2}>
            <Typography variant="body2" color="textSecondary">暂无事件</Typography>
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell style={{ width: 60 }}>类型</TableCell>
                <TableCell style={{ width: 100 }}>Reason</TableCell>
                <TableCell style={{ width: 150 }}>对象</TableCell>
                <TableCell>消息</TableCell>
                <TableCell style={{ width: 50, textAlign: 'right' }}>次数</TableCell>
                <TableCell style={{ width: 140 }}>最后时间</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event, idx) => {
                const isWarning = event.type === 'Warning';
                return (
                  <TableRow
                    key={idx}
                    style={{
                      backgroundColor: isWarning ? '#fff8e1' : undefined,
                    }}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: isWarning ? '#e65100' : '#2e7d32',
                        }}
                      >
                        {event.type}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>
                        {event.reason}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{
                          fontSize: '0.75rem',
                          color: '#666',
                          fontFamily: 'monospace',
                          maxWidth: 140,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {event.involvedObject.kind}/{event.involvedObject.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ fontSize: '0.8rem', color: isWarning ? '#b71c1c' : 'inherit' }}
                      >
                        {event.message}
                      </Typography>
                    </TableCell>
                    <TableCell style={{ textAlign: 'right' }}>
                      <Typography variant="body2" style={{ fontSize: '0.8rem', color: '#666' }}>
                        {event.count}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" style={{ fontSize: '0.75rem', color: '#888' }}>
                        {formatTime(event.lastTimestamp)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
